import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../api/commonAPI';

export interface CartItem {
    tableItemId: number;
    quantity: number;
    product: { productId: number; name: string; price: number; imageUrl: string | null };
}

interface PendingQuantity {
    item: CartItem;
    quantity: number;
    timer?: ReturnType<typeof setTimeout>;
    running: boolean;
    recovering?: boolean;
}

export default function useCartQuantities(itemsUrl: string) {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const itemsRef = useRef<CartItem[]>([]);
    const pending = useRef(new Map<number, PendingQuantity>());
    const errorRef = useRef<string | null>(null);

    const publish = useCallback((nextItems: CartItem[]) => {
        itemsRef.current = nextItems;
        setItems(nextItems);
        setIsSaving(pending.current.size > 0);
    }, []);

    const receiveItems = useCallback((nextItems: CartItem[]) => {
        // Keep local clicks visible while HTTP responses or shared-cart messages arrive.
        const merged = new Map(nextItems.map(item => [item.tableItemId, item]));
        pending.current.forEach((entry, id) => {
            if (entry.quantity <= 0) merged.delete(id);
            else merged.set(id, { ...entry.item, quantity: entry.quantity });
        });
        errorRef.current = null;
        setSyncError(null);
        publish(Array.from(merged.values()));
    }, [publish]);

    useEffect(() => {
        pending.current.clear();
        errorRef.current = null;
        setSyncError(null);
        publish([]);
        const entries = pending.current;
        return () => {
            entries.forEach(entry => clearTimeout(entry.timer));
            entries.clear();
        };
    }, [itemsUrl, publish]);

    const save = useCallback(async (id: number, entry: PendingQuantity) => {
        entry.running = true;
        try {
            // Serialize writes for each item; clicks during a request collapse into the next write.
            while (pending.current.get(id) === entry) {
                const quantity = entry.quantity;
                const response = await axiosInstance.put<CartItem | null>(`${itemsUrl}/${id}`, {
                    productId: entry.item.product.productId, quantity,
                });
                if (pending.current.get(id) !== entry) return;
                if (quantity > 0 && response.data) entry.item = response.data;
                if (entry.quantity !== quantity) continue;
                pending.current.delete(id);
                const remaining = itemsRef.current.filter(item => item.tableItemId !== id);
                if (quantity > 0 && response.data) {
                    // Preserve the existing row order when replacing an item.
                    const existing = itemsRef.current.some(item => item.tableItemId === id);
                    publish(existing ? itemsRef.current.map(item => item.tableItemId === id ? response.data! : item)
                        : [...remaining, response.data]);
                } else publish(remaining);
                return;
            }
        } catch {
            if (pending.current.get(id) !== entry) return;
            entry.recovering = true;
            // Restore the last confirmed quantity, then reconcile ambiguous failures (e.g. timeouts).
            entry.quantity = entry.item.quantity;
            receiveItems(itemsRef.current);
            try {
                const response = await axiosInstance.get<CartItem[]>(itemsUrl);
                if (pending.current.get(id) !== entry) return;
                pending.current.delete(id);
                receiveItems(response.data);
                window.alert('수량을 저장하지 못해 장바구니를 다시 불러왔습니다. 수량을 확인해 주세요.');
            } catch {
                if (pending.current.get(id) !== entry) return;
                pending.current.delete(id);
                errorRef.current = '장바구니를 확인하지 못했습니다. 다시 불러온 뒤 주문해 주세요.';
                setSyncError(errorRef.current);
                publish(itemsRef.current);
            }
        }
    }, [itemsUrl, publish, receiveItems]);

    const changeQuantity = useCallback((item: CartItem, delta: number | 'remove') => {
        if (errorRef.current) return;
        let entry = pending.current.get(item.tableItemId);
        if (entry?.recovering) return;
        const current = entry?.quantity ?? itemsRef.current.find(row => row.tableItemId === item.tableItemId)?.quantity;
        if (current == null || current <= 0) return;
        const quantity = delta === 'remove' ? 0 : Math.max(0, current + delta);
        if (!entry) {
            entry = { item: { ...item, quantity: current }, quantity, running: false };
            pending.current.set(item.tableItemId, entry);
        }
        entry.quantity = quantity;
        receiveItems(itemsRef.current);
        if (!entry.running) {
            clearTimeout(entry.timer);
            const scheduled = entry;
            entry.timer = setTimeout(() => { void save(item.tableItemId, scheduled); }, 150);
        }
    }, [receiveItems, save]);

    const hasPendingChanges = () => pending.current.size > 0 || errorRef.current !== null;
    return { items, receiveItems, changeQuantity, isSaving, syncError, hasPendingChanges };
}
